import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Package } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ToolIcon } from "@/components/iconMap";
import { CROP_TOOL_LIST } from "@/data/tools";
import { GENERIC_TOOLS } from "@/data/moreTools";
import { AI_TOOL_LIST } from "@/data/aiTools";

const MARKET_CROPS = CROP_TOOL_LIST.filter((t) => t.mode === "marketplace");
const LAYOUT_TOOLS = CROP_TOOL_LIST.filter((t) => t.mode !== "marketplace");

function ToolCard({ t, accent = "blue" }) {
  const ring = accent === "indigo" ? "hover:border-indigo-400 hover:shadow-indigo-500/5" : "hover:border-blue-400 hover:shadow-blue-500/5";
  const iconBg = accent === "indigo" ? "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600" : "bg-blue-50 text-blue-600 group-hover:bg-blue-600";
  return (
    <Link to={t.path} data-testid={`hub-card-${t.slug}`}
      className={`group relative bg-white border border-slate-200 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl ${ring} hover:-translate-y-1 flex flex-col`}>
      <div className={`w-12 h-12 rounded-xl ${iconBg} group-hover:text-white transition-colors flex items-center justify-center mb-4`}>
        <ToolIcon name={t.icon} className="w-6 h-6" />
      </div>
      <h3 className="font-heading font-bold text-lg text-slate-900">{t.name}</h3>
      <p className="mt-2 text-sm text-slate-600 flex-1">{t.promise}</p>
      <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${accent === "indigo" ? "text-indigo-600" : "text-blue-600"}`}>
        Open tool <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </span>
    </Link>
  );
}

function Group({ title, tools, accent }) {
  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-5">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((t) => <ToolCard key={t.slug} t={t} accent={accent} />)}
      </div>
    </div>
  );
}

export default function EcommerceHub() {
  return (
    <>
      <Seo title="Free E-commerce Seller Tools — Label Croppers & PDF Tools | Ecroply AI"
        description="Every free Ecroply AI tool in one place: Amazon, Flipkart, Meesho and Shopsy label croppers, merge & A4 layout tools, invoice cropper, PDF utilities and AI listing tools."
        path="/ecommerce-tools" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 right-0 w-[28rem] h-[28rem] bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <span className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-200 border border-blue-400/30 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <Package className="w-4 h-4" /> All free tools
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight max-w-3xl">Ecommerce Seller Tools</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Crop shipping labels, merge and lay out print runs, tidy up PDFs and write better listings — all free, no account needed.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400">
            <ShieldCheck className="w-4 h-4 text-blue-400" /> PDF & image tools run in your browser
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Group title="Shipping label croppers" tools={MARKET_CROPS} accent="blue" />
        <Group title="Combine, layout & invoices" tools={LAYOUT_TOOLS} accent="blue" />
        <Group title="Generic PDF & image tools" tools={GENERIC_TOOLS} accent="blue" />
        <Group title="AI listing tools" tools={AI_TOOL_LIST} accent="indigo" />
      </section>
    </>
  );
}
